<script setup>
import { ref } from 'vue';
import { login } from '../db/auth.js';
import { useRouter } from 'vue-router';


const router = useRouter();
const username = ref('');
const password = ref('');
const loading = ref(false);
const error = ref('');
const showPassword = ref(false);

async function submit() {
  error.value = '';
  loading.value = true;
  const res = await login({ username: username.value.trim(), password: password.value });
  loading.value = false;
  if (!res.ok) {
    error.value = res.message || 'Error de autenticación';
    return;
  }
  router.push('/dashboard');
}
</script>

<template>
  <main class="login-container">
    <div class="login-wrapper">
      <!-- Left Side - Form -->
      <div class="form-side">
        <div class="form-content">
          <div class="header">
            <router-link to="/" class="back-to-landing">
              <span class="material-symbols-rounded">arrow_back</span>
              Volver al inicio
            </router-link>
            <h1 class="welcome-text">Bienvenido a</h1>
            <h2 class="logo-text" style="font-size: 3.5rem; margin-bottom: 0.5rem;">Sushito</h2>
          </div>

          <form @submit.prevent="submit" class="login-form">
            <Transition name="slide-fade">
              <div v-if="error" class="error-message">
                <span class="material-symbols-rounded">error</span>
                <span>{{ error }}</span>
              </div>
            </Transition>

            <!-- Username -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">person</span>
                Usuario
              </label>
              <div class="input-wrapper">
                <span class="input-icon">
                  <span class="material-symbols-rounded">person</span>
                </span>
                <input v-model="username" type="text" placeholder="Ingresa tu usuario" class="input-field"
                  autocomplete="username" />
              </div>
            </div>

            <!-- Password -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">lock</span>
                Contraseña
              </label>
              <div class="input-wrapper password-wrapper">
                <span class="input-icon">
                  <span class="material-symbols-rounded">lock</span>
                </span>
                <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Ingresa tu contraseña"
                  class="input-field" autocomplete="current-password" />
                <button type="button" class="toggle-password" @click="showPassword = !showPassword" tabindex="-1">
                  <span class="material-symbols-rounded">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Submit Button -->
            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="!loading" class="btn-content">
                <span>INICIAR SESIÓN</span>
                <span class="material-symbols-rounded">arrow_forward</span>
              </span>
              <span v-else class="btn-content">
                <span class="loading-spinner"></span>
                <span>Iniciando sesión...</span>
              </span>
            </button>

            <!-- Links -->
            <div class="form-links">
              <router-link to="/forgot-password" class="link-item">
                <span class="material-symbols-rounded">lock_reset</span>
                <span>¿Olvidaste tu contraseña?</span>
              </router-link>
              <div class="divider">
                <span>o</span>
              </div>
              <router-link to="/register" class="link-item signup">
                <span class="material-symbols-rounded">person_add</span>
                <span>Crear cuenta nueva</span>
              </router-link>
            </div>
          </form>
        </div>
      </div>

      <!-- Right Side - Image -->
      <div class="illustration-side">
        <div class="image-container">
          <img src="/img/sushi_hero.png" alt="Sushito" class="login-image" />
          <div class="image-overlay"></div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-container {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
}

.login-wrapper {
  max-width: 1000px;
  width: 100%;
  display: flex;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  overflow: hidden;
  background: rgba(20, 24, 30, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(198, 146, 61, 0.2);
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== LEFT SIDE - FORM ===== */
.form-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 2rem;
  background: transparent;
  overflow: hidden;
}

.form-content {
  width: 100%;
  max-width: 380px;
  animation: fadeIn 0.8s ease-out 0.2s both;
}

.form-content::-webkit-scrollbar {
  width: 4px;
}

.form-content::-webkit-scrollbar-track {
  background: transparent;
}

.form-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header {
  margin-bottom: 1.2rem;
  position: relative;
  text-align: center;
}

.back-to-landing {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  margin-bottom: 1.5rem;
  background: rgba(198, 146, 61, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 12px;
  color: #C6923D;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.back-to-landing:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.5);
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.back-to-landing .material-symbols-rounded {
  font-size: 1.2rem;
}

.welcome-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.2rem;
  letter-spacing: 0.5px;
}

.brand-name {
  font-size: 1.8rem;
  font-weight: 900;
  font-family: 'Playfair Display', 'Cinzel', 'Times New Roman', serif;
  text-transform: uppercase;
  background: linear-gradient(135deg,
      #FFD700 0%,
      #FFE55C 10%,
      #FFD700 20%,
      #F4D03F 35%,
      #FFD700 45%,
      #C6923D 60%,
      #9D6B27 75%,
      #C6923D 85%,
      #FFD700 95%,
      #FFE55C 100%);
  background-size: 300% 300%;
  animation: shimmer 2.5s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.2em;
  margin: 0 0 0.3rem 0;
  text-shadow:
    3px 4px 0px rgba(0, 0, 0, 1),
    5px 7px 12px rgba(0, 0, 0, 0.9),
    7px 10px 20px rgba(0, 0, 0, 0.8),
    0px 2px 4px rgba(255, 215, 0, 0.6),
    0px -2px 2px rgba(114, 68, 16, 0.8),
    inset 0px 1px 0px rgba(255, 255, 255, 0.3);
  filter:
    drop-shadow(3px 5px 10px rgba(0, 0, 0, 0.9)) drop-shadow(1px 2px 4px rgba(255, 215, 0, 0.4));
  position: relative;
  transform: perspective(100px) rotateX(2deg);
}

@keyframes shimmer {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.error-message {
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.1) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-left: 4px solid #ff6b6b;
  color: #ff6b6b;
  font-size: 0.85rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.1);
}

.error-message .material-symbols-rounded {
  font-size: 1.2rem;
}

.activation-hint {
  padding: 0.5rem;
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
  border-radius: 4px;
}

.hint-text {
  font-size: 0.75rem;
  color: var(--text);
  margin-bottom: 0.5rem;
}

.hint-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-link {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.btn-link:hover {
  color: var(--accent-2);
}

/* Input Groups */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.input-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-label .material-symbols-rounded {
  font-size: 1.1rem;
  color: #C6923D;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(198, 146, 61, 0.2);
  border-radius: 14px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.input-wrapper:focus-within {
  border-color: #C6923D;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15),
    0 4px 12px rgba(198, 146, 61, 0.2);
  transform: translateY(-2px);
}

.input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  color: #C6923D;
  pointer-events: none;
}

.input-icon .material-symbols-rounded {
  font-size: 1.3rem;
}

.input-field {
  flex: 1;
  padding: 0.95rem 1rem 0.95rem 0;
  border: none;
  background: transparent;
  font-size: 0.95rem;
  font-family: 'Poppins', sans-serif;
  color: #ffffff;
  transition: all 0.3s;
  outline: none;
}

.input-field::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
}

/* Password Wrapper */
.password-wrapper {
  position: relative;
}

.password-wrapper .input-icon {
  padding-right: 0.5rem;
}

.toggle-password {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  border-radius: 8px;
}

.toggle-password:hover {
  color: #C6923D;
  background: rgba(198, 146, 61, 0.15);
}

.toggle-password:active {
  transform: scale(0.95);
}

.toggle-password .material-symbols-rounded {
  font-size: 1.3rem;
}

/* Activation and Reset Sections */
.activation-section,
.reset-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-2);
  border-radius: 8px;
  border: 1px dashed var(--border);
}

.btn-secondary {
  width: 100%;
  padding: 0.5rem;
  background: transparent;
  color: var(--accent);
  border: 1.5px solid var(--accent);
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  background: rgba(124, 92, 255, 0.1);
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: #000000;
  border: none;
  border-radius: 14px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(198, 146, 61, 0.4);
}

.submit-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.submit-btn:hover:not(:disabled)::before {
  opacity: 1;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(198, 146, 61, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
}

.btn-content .material-symbols-rounded {
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.submit-btn:hover:not(:disabled) .btn-content .material-symbols-rounded {
  transform: translateX(4px);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.3);
  border-top-color: #000000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Links */
.form-links {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
}

.link-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: rgba(198, 146, 61, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.2);
  border-radius: 12px;
  color: #C6923D;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.link-item:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.link-item.signup {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.15) 0%, rgba(223, 192, 94, 0.15) 100%);
  border-color: rgba(198, 146, 61, 0.3);
}

.link-item.signup:hover {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.2) 100%);
}

.link-item .material-symbols-rounded {
  font-size: 1.1rem;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 0.5rem 0;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 500;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(148, 163, 184, 0.3);
}

.divider span {
  padding: 0 1rem;
}

/* ===== RIGHT SIDE - IMAGE ===== */
.illustration-side {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.login-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.6s ease;
}

.login-wrapper:hover .login-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.25) 100%);
  pointer-events: none;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Responsive */
@media (max-width: 968px) {
  .login-container {
    min-height: 100vh;
    height: auto;
    padding: 0;
    align-items: flex-start;
  }

  .login-wrapper {
    flex-direction: column;
    max-width: 100%;
    width: 100%;
    min-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
  }

  .illustration-side {
    min-height: 200px;
    max-height: 200px;
    order: -1;
    flex-shrink: 0;
  }

  .image-container {
    min-height: 200px;
    max-height: 200px;
  }

  .form-side {
    padding: 2rem 1.5rem;
    padding-bottom: 3rem;
    flex: 1;
    overflow: visible;
  }

  .form-content {
    max-width: 100%;
    overflow: visible;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 0;
  }

  .login-wrapper {
    border-radius: 0;
  }

  .illustration-side {
    min-height: 180px;
    max-height: 180px;
  }

  .image-container {
    min-height: 180px;
    max-height: 180px;
  }

  .form-side {
    padding: 1.5rem 1.25rem;
    padding-bottom: 2.5rem;
  }

  .welcome-text {
    font-size: 0.9rem;
  }

  .brand-name {
    font-size: 1.5rem;
  }

  .input-wrapper {
    border-radius: 12px;
  }

  .submit-btn {
    padding: 0.9rem 1.25rem;
    font-size: 0.9rem;
  }

  .back-to-landing {
    padding: 0.5rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.85rem;
  }

  .form-links {
    margin-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>